import { useCallback } from 'react';
import * as React from 'react';
import tw from 'twin.macro';
import 'styled-components/macro';

import { TailwindTransition } from 'component-lib/atoms/tailwind-transition/TailwindTransition';

import { useAbstractDropdownContext } from './AbstractDropdownContext';

export interface AbstractDropdownItemsProps extends Omit<React.ComponentPropsWithoutRef<'div'>, OmittedProps> {
  consumerName?: string;
  providerName?: string;
}

type OmittedProps = 'id' | 'onKeydown' | 'style';

export function AbstractDropdownItems({
  consumerName = 'AbstractDropdownItems',
  providerName,
  ...forwardedProps
}: AbstractDropdownItemsProps) {
  const dropdown = useAbstractDropdownContext(consumerName, providerName);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          dropdown.focusNextItem(event.target);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          dropdown.focusPreviousItem(event.target);
          break;
        case 'Home':
          event.preventDefault();
          dropdown.focusFirstItem();
          break;
        case 'End':
          event.preventDefault();
          dropdown.focusLastItem();
          break;
        case 'Escape':
          event.preventDefault();
          dropdown.focusToggle();
          dropdown.setIsExpanded(false);
          break;
        case 'Tab':
          // The default action is not prevented here for the browser to move
          // the focus to the next focusable element.
          dropdown.setIsExpanded(false);
          break;
        case ' ':
        case 'Enter':
          event.preventDefault();
          event.target.click();
          if (dropdown.shouldCollapseOnItemClick) {
            dropdown.focusToggle();
          }
          break;
      }
    },
    [dropdown],
  );

  return (
    <TailwindTransition
      in={dropdown.isExpanded}
      styles={{
        enter: tw`opacity-0 origin-top-left scale-95 transform`,
        enterActive: tw`duration-fast ease-out opacity-100 origin-top-left scale-100 transform transition`,
        exit: tw`opacity-100 origin-top-left scale-100 transform`,
        exitActive: tw`duration-fast ease-in opacity-0 origin-top-left scale-95 transform transition`,
      }}
      unmountOnExit
    >
      {(_, twStyle) => (
        <div
          {...forwardedProps}
          ref={dropdown.setMenuElement}
          css={[tw`bg-white py-[0.75rem] rounded-sm shadow-card z-overlay`, twStyle]}
          id={dropdown.itemsID}
          onKeyDown={handleKeyDown}
          style={dropdown.menuStyle}
        />
      )}
    </TailwindTransition>
  );
}
